// Anular un comprobante YA AUTORIZADO por el SRI — el hermano de DescartarDialog.
//
// Descartar es para lo que nunca llegó al SRI; esto es para lo que sí llegó y
// luego se anuló: en el portal del SRI (consumidor final o anulación aceptada
// por el receptor) o con una nota de crédito autorizada.
//
// `fin_comprobante_anular` (438 + 906) valida la vía, exige nivel 5, rechaza si
// hay cobros vivos y —salvo vía NC, cuya propia NC ya reversa la venta— genera
// el contra-asiento. Sin eso el mayor seguía con el ingreso y la CxC de una
// factura que la cartera, el F104 y el ATS ya daban por anulada.
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };

export interface DocAnulable {
  id: string;
  serie_comprobante?: string | null;
  cliente?: string | null;
  importe_total?: number;
}

interface Resultado {
  via: string; nota_credito: string | null;
  asiento_original: number | null; asiento_reverso: number | null; fecha_reverso: string | null;
}

export function AnularDialog({ doc, onClose, onDone }: {
  doc: DocAnulable;
  onClose: () => void;
  onDone: () => void;
}) {
  const [via, setVia] = useState<'portal_sri' | 'nota_credito'>('portal_sri');
  const [motivo, setMotivo] = useState('');
  const [verificado, setVerificado] = useState(false);

  const anular = useMutation({
    mutationFn: async () =>
      (await apiClient.post('/rpc/fin_comprobante_anular', {
        _ticket_id: doc.id, _via: via, _motivo: motivo.trim() || null,
      }, FIN)).data as Resultado,
    onSuccess: (r) => {
      const asiento = r.asiento_reverso
        ? ` Se registró el asiento de reverso nº ${r.asiento_reverso} (del nº ${r.asiento_original}) con fecha ${r.fecha_reverso}.`
        : r.via === 'nota_credito' ? ` La nota de crédito ${r.nota_credito ?? ''} ya reversa la venta en contabilidad.` : '';
      toast.success(`Comprobante ${doc.serie_comprobante ?? ''} anulado.${asiento}`, { duration: 8000 });
      onDone(); onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const campo = 'w-full rounded-md border bg-background px-2.5 py-1.5 text-sm';
  const corto = via === 'portal_sri' && motivo.trim().length < 10;
  const usd = (v: number) => v.toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });

  return (
    <PanelIntegrado title="Anular comprobante autorizado" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          <strong className="font-mono">{doc.serie_comprobante}</strong>
          {doc.cliente && <> · {doc.cliente}</>}
          {doc.importe_total != null && <> · {usd(Number(doc.importe_total))}</>}
        </p>
        <p className="text-xs text-muted-foreground">
          Esto no anula nada ante el SRI: registra en el ERP una anulación que <strong>ya ocurrió</strong> allá.
          La factura sale de la cartera, del Formulario 104 y se contabiliza su reverso. Queda a tu nombre.
        </p>

        <fieldset className="space-y-2">
          <legend className="text-xs text-muted-foreground mb-1">¿Cómo se anuló?</legend>
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" className="mt-0.5" checked={via === 'portal_sri'} onChange={() => setVia('portal_sri')} />
            <span><strong>En el portal del SRI.</strong> Va al ATS como anulada y se genera el asiento de reverso.</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" className="mt-0.5" checked={via === 'nota_credito'} onChange={() => setVia('nota_credito')} />
            <span><strong>Con una nota de crédito autorizada</strong> que la modifica. La NC ya reversa la venta:
              no se genera otro asiento.</span>
          </label>
        </fieldset>

        <label className="block text-xs text-muted-foreground">
          {via === 'portal_sri' ? 'Motivo y constancia de la anulación en el portal *' : 'Observación (opcional)'}
          <textarea className={`${campo} mt-1`} rows={3} value={motivo} autoFocus
                    placeholder={via === 'portal_sri' ? 'Quién la anuló, cuándo y por qué (mínimo 10 caracteres)' : ''}
                    onChange={(e) => setMotivo(e.target.value)} />
          {corto && motivo.length > 0 && (
            <span className="text-[11px] text-amber-600">Faltan {10 - motivo.trim().length} caracteres</span>
          )}
        </label>

        {via === 'portal_sri' && (
          <label className="flex items-start gap-2 text-sm rounded-md border border-amber-500/50 bg-amber-500/5 p-3">
            <input type="checkbox" className="mt-0.5" checked={verificado}
                   onChange={(e) => setVerificado(e.target.checked)} />
            <span>Consulté la clave de acceso en el SRI y el comprobante figura como <strong>ANULADO</strong>.</span>
          </label>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <Button size="sm" variant="destructive" isLoading={anular.isPending}
                  disabled={corto || (via === 'portal_sri' && !verificado)}
                  onClick={() => anular.mutate()}>
            Anular
          </Button>
        </div>
      </div>
    </PanelIntegrado>
  );
}
